import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DndContext,
    closestCorners,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    defaultDropAnimationSideEffects,
    useDroppable,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useSnapshotShare from '../../hooks/useSnapshotShare';
import useSnapshotHistory from '../../hooks/useSnapshotHistory';
import SnapshotToast from '../../components/SnapshotToast';
import HistoryDrawer from '../../components/HistoryDrawer';
import ConfirmationModal from '../../components/ConfirmationModal';
import ThoughtBubbleIcon from '../../components/ThoughtBubbleIcon';
import { Plus, Share2, Home, Trash2, GripVertical, Pencil, CornerDownLeft, Clock } from 'lucide-react';

// --- Components ---

const TaskCard = ({ id, content, onDelete, onUpdate }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(content);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        touchAction: 'none', // Prevent scrolling while dragging
    };

    const handleSave = () => {
        if (editContent.trim()) {
            onUpdate(id, editContent.trim());
        } else {
            setEditContent(content); // Revert if empty
        }
        setIsEditing(false);
    };

    const handleKeyDown = (e) => {
        // Stop propagation to prevent dnd-kit from picking up the event
        e.stopPropagation();

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSave();
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={`bg-white p-4 rounded-lg shadow-sm border border-gray-200 group hover:shadow-md transition-shadow mb-3 flex items-start gap-2 cursor-grab active:cursor-grabbing ${isDragging ? 'ring-2 ring-blue-400 rotate-2' : ''}`}
        >
            <div className="mt-1 text-gray-400 hover:text-gray-600">
                <GripVertical size={16} />
            </div>

            {isEditing ? (
                <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={handleKeyDown}
                    onPointerDown={(e) => e.stopPropagation()} // Allow interaction with input
                    className="flex-grow text-sm text-gray-800 border rounded p-1 focus:ring-2 focus:ring-blue-500 outline-none resize-none bg-gray-50"
                    autoFocus
                    rows={Math.max(2, Math.ceil(editContent.length / 30))}
                    onClick={(e) => e.stopPropagation()}
                />
            ) : (
                <p className="flex-grow text-gray-800 text-sm leading-relaxed break-words pointer-events-none">{content}</p>
            )}

            <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity self-center">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsEditing(true);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="text-gray-300 hover:text-blue-500 transition-colors"
                    aria-label="Edit task"
                >
                    <Pencil size={18} />
                </button>
            </div>
        </div>
    );
};

const Column = ({ id, title, tasks, onDeleteTask, onUpdateTask }) => {
    const { setNodeRef } = useDroppable({
        id: id,
    });

    return (
        <div ref={setNodeRef} className="flex flex-col h-full bg-gray-100 rounded-xl p-4 min-w-[280px] border border-gray-200/60">
            <h2 className="font-bold text-gray-700 mb-4 flex items-center justify-between">
                {title}
                <span className="bg-gray-200 text-gray-500 text-xs px-2 py-1 rounded-full">{tasks.length}</span>
            </h2>
            <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                <div className="flex-grow min-h-[100px]">
                    {tasks.map((task) => (
                        <TaskCard key={task.id} id={task.id} content={task.content} onDelete={onDeleteTask} onUpdate={onUpdateTask} />
                    ))}
                </div>
            </SortableContext>
        </div>
    );
};

const TrashZone = () => {
    const { setNodeRef, isOver } = useDroppable({
        id: 'trash-zone',
    });

    return (
        <div
            ref={setNodeRef}
            className={`w-full max-w-2xl mx-auto mt-4 p-6 rounded-xl border-2 border-dashed transition-colors flex items-center justify-center gap-3 ${isOver ? 'border-red-500 bg-red-50 text-red-600' : 'border-gray-200 text-gray-400'}`}
        >
            <Trash2 size={24} />
            <span className="font-medium">Drag here to delete</span>
        </div>
    );
};

// --- Main App ---
const LOCAL_STORAGE_KEY = 'kanbanIt';

const KanbanItApp = () => {
    const navigate = useNavigate();
    const [columns, setColumns] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        // If snapshot URL, return default (hook will update it)
        if (params.get('s')) {
            return {
                todo: [{ id: 't1', content: 'Welcome to Kanban-It! 👋' }],
                doing: [{ id: 't2', content: 'Drag me around' }],
                done: [{ id: 't3', content: 'Share your board 🚀' }],
            };
        }
        // Try Local Storage
        try {
            const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (stored) return JSON.parse(stored);
        } catch (e) {
            console.error("Failed to load kanban data", e);
        }
        // Return Default
        return {
            todo: [{ id: 't1', content: 'Welcome to Kanban-It! 👋' }],
            doing: [{ id: 't2', content: 'Drag me around' }],
            done: [{ id: 't3', content: 'Share your board 🚀' }],
        };
    });

    const [inputValue, setInputValue] = useState('');
    const [activeId, setActiveId] = useState(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [isClearModalOpen, setIsClearModalOpen] = useState(false);

    // Save to LocalStorage (only if not snapshot)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (!params.get('s')) {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(columns));
        }
    }, [columns]);

    // Use shared snapshot hook (Blue theme)
    const { handleShare, showToast, setShowToast } = useSnapshotShare(columns, setColumns, 's');

    // Snapshot History Hook
    const { history, saveSnapshot, restoreSnapshot, deleteSnapshot, clearHistory } = useSnapshotHistory('kanbanIt', columns);

    const handleShareWithSnapshot = () => {
        saveSnapshot('Shared via Link');
        handleShare();
    };

    const handleRestore = (id) => {
        const data = restoreSnapshot(id);
        if (data) {
            setColumns(data);
            setIsHistoryOpen(false);
        }
    };

    // Sensors for Drag and Drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Increased for mobile tolerance
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
            filter: (event) => {
                const target = event.target;
                return !['INPUT', 'TEXTAREA'].includes(target.tagName);
            },
        })
    );

    const submitTask = () => {
        if (inputValue.trim()) {
            const newTask = {
                id: `task-${Date.now()}`,
                content: inputValue.trim(),
            };
            setColumns(prev => ({
                ...prev,
                todo: [newTask, ...prev.todo], // Add to top of To Do
            }));
            setInputValue('');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') submitTask();
    };

    const handleDeleteTask = (taskId) => {
        setColumns(prev => {
            const newCols = { ...prev };
            Object.keys(newCols).forEach(colId => {
                newCols[colId] = newCols[colId].filter(t => t.id !== taskId);
            });
            return newCols;
        });
    };

    const handleUpdateTask = (taskId, newContent) => {
        setColumns(prev => {
            const newCols = { ...prev };
            Object.keys(newCols).forEach(colId => {
                newCols[colId] = newCols[colId].map(t =>
                    t.id === taskId ? { ...t, content: newContent } : t
                );
            });
            return newCols;
        });
    };

    const handleClearBoard = () => {
        setIsClearModalOpen(true);
    };

    const confirmClearBoard = () => {
        setColumns({
            todo: [],
            doing: [],
            done: []
        });
        setIsClearModalOpen(false);
    };

    // Drag and Drop Logic
    const findContainer = (id) => {
        if (id in columns) return id;
        return Object.keys(columns).find(key => columns[key].find(t => t.id === id));
    };

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragOver = (event) => {
        const { active, over } = event;
        const overId = over?.id;

        if (!overId || active.id === overId || overId === 'trash-zone') return;

        const activeContainer = findContainer(active.id);
        const overContainer = findContainer(overId);

        if (!activeContainer || !overContainer || activeContainer === overContainer) return;

        setColumns((prev) => {
            const activeItems = prev[activeContainer];
            const overItems = prev[overContainer];
            const activeIndex = activeItems.findIndex((t) => t.id === active.id);
            const overIndex = overItems.findIndex((t) => t.id === overId);

            let newIndex;
            if (overId in prev) {
                newIndex = overItems.length + 1;
            } else {
                const isBelowOverItem =
                    over &&
                    active.rect.current.translated &&
                    active.rect.current.translated.top > over.rect.top + over.rect.height;

                const modifier = isBelowOverItem ? 1 : 0;
                newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
            }

            return {
                ...prev,
                [activeContainer]: [
                    ...prev[activeContainer].filter((item) => item.id !== active.id),
                ],
                [overContainer]: [
                    ...prev[overContainer].slice(0, newIndex),
                    activeItems[activeIndex],
                    ...prev[overContainer].slice(newIndex, prev[overContainer].length),
                ],
            };
        });
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        // Handle Trash Zone Drop
        if (over && over.id === 'trash-zone') {
            handleDeleteTask(active.id);
            setActiveId(null);
            return;
        }

        const activeContainer = findContainer(active.id);
        const overContainer = findContainer(over?.id);

        if (
            activeContainer &&
            overContainer &&
            activeContainer === overContainer
        ) {
            const activeIndex = columns[activeContainer].findIndex((t) => t.id === active.id);
            const overIndex = columns[overContainer].findIndex((t) => t.id === over.id);

            if (activeIndex !== overIndex) {
                setColumns((prev) => ({
                    ...prev,
                    [activeContainer]: arrayMove(prev[activeContainer], activeIndex, overIndex),
                }));
            }
        }

        setActiveId(null);
    };

    const dropAnimation = {
        sideEffects: defaultDropAnimationSideEffects({
            styles: {
                active: {
                    opacity: '0.5',
                },
            },
        }),
    };

    // Helper to find active task content
    const activeTask = activeId ? (
        columns.todo.find(t => t.id === activeId) ||
        columns.doing.find(t => t.id === activeId) ||
        columns.done.find(t => t.id === activeId)
    ) : null;

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans text-gray-900">
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 px-4 md:px-6 py-4 flex items-center justify-between sticky top-0 z-50">
                <div
                    className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity w-1/3"
                    onClick={() => navigate('/')}
                >
                    <ThoughtBubbleIcon className="fill-blue-600 text-blue-600 shrink-0" size={24} />
                    <span className="text-sm md:text-xl font-bold md:font-extrabold text-blue-600 tracking-tight font-rounded">
                        Thoughtblink
                    </span>
                </div>

                <div className="flex justify-center w-1/3">
                    <h1 className="text-lg md:text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        Kanban-It
                    </h1>
                </div>

                <div className="flex items-center justify-end w-1/3">
                    <button
                        onClick={handleShareWithSnapshot}
                        className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-full transition-all"
                        title="Share Board"
                    >
                        <Share2 size={20} />
                    </button>
                    <button
                        onClick={() => setIsHistoryOpen(true)}
                        className="p-2 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-all"
                        title="Version History"
                    >
                        <Clock size={20} />
                    </button>
                    <button
                        onClick={handleClearBoard}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-all"
                        title="Clear Board"
                    >
                        <Trash2 size={20} />
                    </button>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-grow p-4 md:p-8 max-w-7xl mx-auto w-full flex flex-col gap-8">

                {/* The Blink Input */}
                <div className="w-full max-w-2xl mx-auto relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Plus className="text-slate-400" size={24} />
                    </div>
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Capture a task..."
                        className="w-full pl-12 pr-12 py-4 bg-white border-2 border-slate-200 rounded-xl text-lg shadow-sm placeholder-slate-400 focus:border-slate-400 focus:outline-none transition-all"
                    />
                    <button
                        onClick={submitTask}
                        className="absolute inset-y-0 right-4 flex items-center justify-center text-slate-400 hover:text-blue-600 cursor-pointer transition-colors"
                        title="Add Task"
                    >
                        <CornerDownLeft size={20} />
                    </button>
                </div>

                {/* Kanban Board */}
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCorners}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                >
                    <TrashZone />

                    <div className="flex flex-col md:flex-row gap-6 items-center md:items-start md:justify-center w-full h-full mt-4">
                        <Column id="todo" title="To Do" tasks={columns.todo} onDeleteTask={handleDeleteTask} onUpdateTask={handleUpdateTask} />
                        <Column id="doing" title="Doing" tasks={columns.doing} onDeleteTask={handleDeleteTask} onUpdateTask={handleUpdateTask} />
                        <Column id="done" title="Done" tasks={columns.done} onDeleteTask={handleDeleteTask} onUpdateTask={handleUpdateTask} />
                    </div>

                    <DragOverlay dropAnimation={dropAnimation}>
                        {activeTask ? (
                            <div className="bg-white p-4 rounded-lg shadow-xl border border-blue-500/50 rotate-2 cursor-grabbing opacity-90 w-[280px]">
                                <div className="flex items-start gap-2">
                                    <GripVertical size={16} className="text-gray-400 mt-1" />
                                    <p className="text-gray-800 text-sm leading-relaxed break-words">{activeTask.content}</p>
                                </div>
                            </div>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            </main>
            <SnapshotToast isVisible={showToast} onClose={() => setShowToast(false)} color="blue" />
            <HistoryDrawer
                isOpen={isHistoryOpen}
                onClose={() => setIsHistoryOpen(false)}
                history={history}
                onRestore={handleRestore}
                onDelete={deleteSnapshot}
                onClearHistory={clearHistory}
            />
            <ConfirmationModal
                isOpen={isClearModalOpen}
                onClose={() => setIsClearModalOpen(false)}
                onConfirm={confirmClearBoard}
                title="Clear Kanban Board?"
                message="Are you sure you want to delete all tasks? This action cannot be undone."
                confirmText="Yes, Clear All"
            />
        </div>
    );
};

export default KanbanItApp;
